import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { localizeFilterLabelAndOptions } from '../../util/filtersLocalizationHelpers';
import { getSelectedSpaceTypeKey, getSpaceTypeQueryParamValue } from '../../util/spaceTypes';

import { ChevronIcon, SlidersIcon, CloseIcon } from './Icons';
import {
  getFilterQueryParamName,
  parseSelectedOptions,
  formatMultiValue,
  parsePriceValue,
  arePriceRangesEqual,
  compactQueryParams,
  normalizePriceRange,
  orderSpaceTypeOptions,
  serializePriceRange,
} from './filtersModalUtils';
import PriceRangeSection from './PriceRangeSection';
import TextChip from './TextChip';
import IconChip from './IconChip';
import ActivityCard from './ActivityCard';
import QuickFilterCard from './QuickFilterCard';
import AccordionSection from './AccordionSection';

import { ICONS_BY_OPTION_KEY } from '../../util/filterIcons';

import css from './SearchFiltersModalV2.module.css';

const ESCAPE_KEY_CODE = 27;
const MODAL_ID = 'SearchFiltersModalV2';
const ANIMATION_MS = 220;
const MANAGED_FILTER_IDS = [
  'space_type_more_filters',
  'price',
  'area_c',
  'actividad_c',
  'amenities_uupo',
  'amenities_pop',
  'volumen',
  'orientacion',
  'altura',
];
const ACTIVITY_CARD_KEYS = [
  'actividad_c1',
  'actividad_c2',
  'actividad_c3',
  'actividad_c4',
  'actividad_c5',
  'actividad_c6',
  'actividad_c8',
  'actividad_c12',
  'actividad_c13',
  'actividad_c14',
  'actividad_c15',
  'actividad_c16',
];
const LOCAL_FEATURE_KEYS = [
  'amenities_uupo2',
  'amenities_uupo3',
  'amenities_uupo6',
  'amenities_uupo11',
  'amenities_uupo12',
  'amenities_uupo13',
  'amenities_uupo16',
  'amenities_uupo17',
];
const BRAND_FEATURE_KEYS = [
  'amenities_pop1',
  'amenities_pop2',
  'amenities_pop5',
  'amenities_pop10',
  'amenities_pop12',
];
const QUICK_FILTERS = [
  { filterId: 'amenities_uupo', optionKey: 'amenities_uupo2' },
  { filterId: 'amenities_uupo', optionKey: 'amenities_uupo17' },
  { filterId: 'amenities_uupo', optionKey: 'amenities_uupo16' },
  { filterId: 'amenities_pop', optionKey: 'amenities_pop1' },
];
const SearchFiltersModalV2 = props => {
  const {
    isOpen,
    onClose,
    onApplySearchParams,
    currentSearchParams,
    filterConfig,
    intl,
    onManageDisableScrolling,
    priceDistribution,
    priceRange,
  } = props;

  const safeFilterConfig = Array.isArray(filterConfig) ? filterConfig : [];
  const localizedFilters = localizeFilterLabelAndOptions(
    safeFilterConfig.filter(configItem => configItem.searchFilter !== false),
    intl
  );
  const managedFilters = MANAGED_FILTER_IDS.reduce((acc, filterId) => {
    const filterItem = localizedFilters.find(configItem => configItem.id === filterId);
    return filterItem ? { ...acc, [filterId]: filterItem } : acc;
  }, {});
  const portalRoot =
    typeof document !== 'undefined'
      ? document.getElementById('portal-root') || document.body
      : null;
  const configuredPriceFilter = managedFilters.price || null;
  const configuredPriceRange =
    configuredPriceFilter && configuredPriceFilter.config
      ? {
          minPrice: configuredPriceFilter.config.min,
          maxPrice: configuredPriceFilter.config.max,
          step: configuredPriceFilter.config.step,
        }
      : { minPrice: 0, maxPrice: 5000, step: 5 };
  const initialLivePriceRange = normalizePriceRange(priceRange, configuredPriceRange);
  const priceQueryParamName = getFilterQueryParamName(configuredPriceFilter);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);
  const [draftParams, setDraftParams] = useState(currentSearchParams || {});
  const [livePriceRange, setLivePriceRange] = useState(initialLivePriceRange);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllLocalFeatures, setShowAllLocalFeatures] = useState(false);
  const [showAllBrandFeatures, setShowAllBrandFeatures] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    volumen: true,
    orientacion: false,
    altura: false,
  });
  const previousLivePriceRangeRef = useRef(initialLivePriceRange);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setDraftParams(currentSearchParams || {});
      setShowAllActivities(false);
      setShowAllLocalFeatures(false);
      setShowAllBrandFeatures(false);
      setExpandedSections({
        volumen: true,
        orientacion: false,
        altura: false,
      });

      const rafId = window.requestAnimationFrame(() => {
        setIsVisible(true);
      });

      return () => window.cancelAnimationFrame(rafId);
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
    }, ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, currentSearchParams]);

  useEffect(() => {
    setLivePriceRange(prevState =>
      arePriceRangesEqual(prevState, initialLivePriceRange) ? prevState : initialLivePriceRange
    );
  }, [initialLivePriceRange.maxPrice, initialLivePriceRange.minPrice, initialLivePriceRange.step]);

  useEffect(() => {
    onManageDisableScrolling(MODAL_ID, shouldRender);

    return () => {
      onManageDisableScrolling(MODAL_ID, false);
    };
  }, [onManageDisableScrolling, shouldRender]);

  useEffect(() => {
    if (!shouldRender || typeof document === 'undefined') {
      return undefined;
    }

    const onKeyUp = event => {
      if (event.keyCode === ESCAPE_KEY_CODE) {
        onClose();
      }
    };

    window.document.body.addEventListener('keyup', onKeyUp);
    return () => {
      window.document.body.removeEventListener('keyup', onKeyUp);
    };
  }, [onClose, shouldRender]);

  useEffect(() => {
    if (!priceQueryParamName || !shouldRender) {
      return;
    }

    const nextLivePriceRange = normalizePriceRange(livePriceRange, configuredPriceRange);
    const previousLivePriceRange = previousLivePriceRangeRef.current || nextLivePriceRange;

    setDraftParams(prevState => {
      const parsedDraftPrice = parsePriceValue(prevState[priceQueryParamName]);

      if (!parsedDraftPrice) {
        return prevState;
      }

      const draftMatchesPreviousLiveRange = arePriceRangesEqual(
        { ...parsedDraftPrice, step: previousLivePriceRange.step },
        previousLivePriceRange
      );
      const nextPriceRange = draftMatchesPreviousLiveRange
        ? nextLivePriceRange
        : normalizePriceRange(parsedDraftPrice, nextLivePriceRange);
      const nextSerializedPrice = serializePriceRange(nextPriceRange);

      return prevState[priceQueryParamName] === nextSerializedPrice
        ? prevState
        : {
            ...prevState,
            [priceQueryParamName]: nextSerializedPrice,
          };
    });

    previousLivePriceRangeRef.current = nextLivePriceRange;
  }, [
    configuredPriceRange.maxPrice,
    configuredPriceRange.minPrice,
    configuredPriceRange.step,
    livePriceRange.maxPrice,
    livePriceRange.minPrice,
    livePriceRange.step,
    priceQueryParamName,
    shouldRender,
  ]);

  if (!shouldRender || !portalRoot) {
    return null;
  }

  const getFilter = filterId => managedFilters[filterId] || null;
  const getOptions = filterId => {
    const filterItem = getFilter(filterId);
    return filterItem && filterItem.config && Array.isArray(filterItem.config.options)
      ? filterItem.config.options
      : [];
  };
  const getQueryParamName = filterId => {
    return getFilterQueryParamName(getFilter(filterId));
  };
  const getSelectedMultiValues = filterId => {
    return parseSelectedOptions(draftParams[getQueryParamName(filterId)]);
  };
  const isMultiOptionSelected = (filterId, optionKey) => {
    return getSelectedMultiValues(filterId).includes(optionKey);
  };
  const isSingleOptionSelected = (filterId, optionKey) => {
    return draftParams[getQueryParamName(filterId)] === optionKey;
  };
  const updateMultiSelect = (filterId, optionKey) => {
    const filterItem = getFilter(filterId);
    const queryParamName = getQueryParamName(filterId);
    const currentValues = getSelectedMultiValues(filterId);
    const nextValues = currentValues.includes(optionKey)
      ? currentValues.filter(value => value !== optionKey)
      : [...currentValues, optionKey];
    const nextValue = formatMultiValue(
      nextValues,
      filterItem && filterItem.config && filterItem.config.searchMode
    );

    setDraftParams(prevState => ({
      ...prevState,
      [queryParamName]: nextValue,
    }));
  };
  const updateSingleSelect = (filterId, optionKey) => {
    const queryParamName = getQueryParamName(filterId);
    const nextValue = draftParams[queryParamName] === optionKey ? null : optionKey;

    setDraftParams(prevState => ({
      ...prevState,
      [queryParamName]: nextValue,
    }));
  };
  const updateSpaceType = optionKey => {
    const queryParamName = getQueryParamName('space_type_more_filters');
    const nextValue = getSpaceTypeQueryParamValue(optionKey, getOptions('space_type_more_filters'));

    setDraftParams(prevState => ({
      ...prevState,
      [queryParamName]: nextValue,
    }));
  };
  const toggleSection = sectionKey => {
    setExpandedSections(prevState => ({
      ...prevState,
      [sectionKey]: !prevState[sectionKey],
    }));
  };

  const priceFilter = configuredPriceFilter;
  const priceDefaults = normalizePriceRange(livePriceRange, configuredPriceRange);
  const parsedPrice = parsePriceValue(
    priceQueryParamName ? draftParams[priceQueryParamName] : null
  );
  const currentPrice = normalizePriceRange(parsedPrice, priceDefaults);
  const currentSpaceTypeKey = getSelectedSpaceTypeKey(
    draftParams[getQueryParamName('space_type_more_filters')],
    getOptions('space_type_more_filters')
  );

  const setPriceRange = (nextMin, nextMax) => {
    const safeMin = Math.max(
      priceDefaults.minPrice,
      Math.min(nextMin, nextMax - priceDefaults.step)
    );
    const safeMax = Math.min(
      priceDefaults.maxPrice,
      Math.max(nextMax, safeMin + priceDefaults.step)
    );

    if (!priceQueryParamName) {
      return;
    }

    setDraftParams(prevState => ({
      ...prevState,
      [priceQueryParamName]: `${safeMin},${safeMax}`,
    }));
  };

  const managedQueryParamNames = MANAGED_FILTER_IDS.reduce((acc, filterId) => {
    const queryParamName = getQueryParamName(filterId);
    return queryParamName ? [...acc, queryParamName] : acc;
  }, []);
  const hasActiveManagedFilters = managedQueryParamNames.some(queryParamName => {
    return draftParams[queryParamName] != null;
  });
  const resetManagedFilters = () => {
    const nextParams = { ...draftParams };
    managedQueryParamNames.forEach(queryParamName => {
      delete nextParams[queryParamName];
    });
    setDraftParams(nextParams);
  };
  const handleApply = () => {
    onApplySearchParams(compactQueryParams(draftParams));
    onClose();
  };

  const spaceTypeFilter = getFilter('space_type_more_filters');

  return ReactDOM.createPortal(
    <div
      className={classNames(css.root, {
        [css.rootVisible]: isVisible,
      })}
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={classNames(css.panel, {
          [css.panelVisible]: isVisible,
        })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="uupo-search-filters-modal-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <div className={css.header}>
          <h2 id="uupo-search-filters-modal-title" className={css.title}>
            <FormattedMessage id="SearchFiltersModalV2.title" />
          </h2>
          <button
            type="button"
            className={css.closeButton}
            title={intl.formatMessage({ id: 'SearchFiltersModalV2.close' })}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={css.body}>
          <section className={css.section}>
            <h3 className={css.sectionTitle}>
              <FormattedMessage id="SearchFiltersModalV2.quickTitle" />
            </h3>
            <div className={css.quickCards}>
              {QUICK_FILTERS.map(item => {
                const option = getOptions(item.filterId).find(
                  optionItem => optionItem.key === item.optionKey
                );
                return (
                  <QuickFilterCard
                    key={`${item.filterId}-${item.optionKey}`}
                    option={option}
                    selected={isMultiOptionSelected(item.filterId, item.optionKey)}
                    iconSrc={ICONS_BY_OPTION_KEY[item.optionKey]}
                    onClick={() => updateMultiSelect(item.filterId, item.optionKey)}
                  />
                );
              })}
            </div>
          </section>

          {spaceTypeFilter ? (
            <section className={css.section}>
              <h3 className={css.sectionTitle}>{spaceTypeFilter.label}</h3>
              <div className={css.segmentedControl}>
                {orderSpaceTypeOptions(getOptions('space_type_more_filters')).map(option => (
                  <TextChip
                    key={option.key}
                    option={option}
                    compact
                    selected={currentSpaceTypeKey === option.key}
                    onClick={() => updateSpaceType(option.key)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {priceFilter ? (
            <PriceRangeSection
              priceDefaults={priceDefaults}
              currentPrice={currentPrice}
              priceDistribution={priceDistribution}
              onPriceChange={setPriceRange}
              intl={intl}
              label={priceFilter.label}
            />
          ) : null}

          <section className={css.section}>
            <h3 className={css.sectionTitle}>
              {getFilter('area_c') ? getFilter('area_c').label : null}
            </h3>
            <div className={css.chipWrap}>
              {getOptions('area_c').map(option => (
                <TextChip
                  key={option.key}
                  option={option}
                  selected={isMultiOptionSelected('area_c', option.key)}
                  onClick={() => updateMultiSelect('area_c', option.key)}
                />
              ))}
            </div>
          </section>

          <section className={css.section}>
            <h3 className={css.sectionTitle}>
              {getFilter('actividad_c') ? getFilter('actividad_c').label : null}
            </h3>
            <div className={css.activityGrid}>
              {(showAllActivities ? ACTIVITY_CARD_KEYS : ACTIVITY_CARD_KEYS.slice(0, 8)).map(
                optionKey => {
                  const option = getOptions('actividad_c').find(
                    optionItem => optionItem.key === optionKey
                  );
                  if (!option) {
                    return null;
                  }
                  return (
                    <ActivityCard
                      key={optionKey}
                      option={option}
                      selected={isMultiOptionSelected('actividad_c', optionKey)}
                      iconSrc={ICONS_BY_OPTION_KEY[optionKey]}
                      onClick={() => updateMultiSelect('actividad_c', optionKey)}
                    />
                  );
                }
              )}
            </div>
            <button
              type="button"
              className={css.moreButton}
              onClick={() => setShowAllActivities(prevState => !prevState)}
            >
              <FormattedMessage
                id={
                  showAllActivities
                    ? 'SearchFiltersModalV2.showLess'
                    : 'SearchFiltersModalV2.showMore'
                }
              />
              <ChevronIcon isOpen={showAllActivities} />
            </button>
          </section>

          <section className={css.section}>
            <h3 className={css.sectionTitle}>
              {getFilter('amenities_uupo') ? getFilter('amenities_uupo').label : null}
            </h3>
            <div className={css.chipWrap}>
              {(showAllLocalFeatures
                ? getOptions('amenities_uupo')
                : getOptions('amenities_uupo').filter(option =>
                    LOCAL_FEATURE_KEYS.includes(option.key)
                  )
              ).map(option => (
                <IconChip
                  key={option.key}
                  option={option}
                  selected={isMultiOptionSelected('amenities_uupo', option.key)}
                  iconSrc={ICONS_BY_OPTION_KEY[option.key]}
                  onClick={() => updateMultiSelect('amenities_uupo', option.key)}
                />
              ))}
            </div>
            <button
              type="button"
              className={css.moreButton}
              onClick={() => setShowAllLocalFeatures(prevState => !prevState)}
            >
              <FormattedMessage
                id={
                  showAllLocalFeatures
                    ? 'SearchFiltersModalV2.showLess'
                    : 'SearchFiltersModalV2.showMore'
                }
              />
              <ChevronIcon isOpen={showAllLocalFeatures} />
            </button>
          </section>

          <section className={css.section}>
            <h3 className={css.sectionTitle}>
              {getFilter('amenities_pop') ? getFilter('amenities_pop').label : null}
            </h3>
            <div className={css.chipWrap}>
              {(showAllBrandFeatures
                ? getOptions('amenities_pop')
                : getOptions('amenities_pop').filter(option =>
                    BRAND_FEATURE_KEYS.includes(option.key)
                  )
              ).map(option => (
                <IconChip
                  key={option.key}
                  option={option}
                  selected={isMultiOptionSelected('amenities_pop', option.key)}
                  iconSrc={ICONS_BY_OPTION_KEY[option.key]}
                  onClick={() => updateMultiSelect('amenities_pop', option.key)}
                />
              ))}
            </div>
            <button
              type="button"
              className={css.moreButton}
              onClick={() => setShowAllBrandFeatures(prevState => !prevState)}
            >
              <FormattedMessage
                id={
                  showAllBrandFeatures
                    ? 'SearchFiltersModalV2.showLess'
                    : 'SearchFiltersModalV2.showMore'
                }
              />
              <ChevronIcon isOpen={showAllBrandFeatures} />
            </button>
          </section>

          <AccordionSection
            label={getFilter('volumen') ? getFilter('volumen').label : ''}
            isExpanded={expandedSections.volumen}
            onToggle={() => toggleSection('volumen')}
          >
            {getOptions('volumen').map(option => (
              <TextChip
                key={option.key}
                option={option}
                selected={isMultiOptionSelected('volumen', option.key)}
                onClick={() => updateMultiSelect('volumen', option.key)}
              />
            ))}
          </AccordionSection>

          <AccordionSection
            label={getFilter('orientacion') ? getFilter('orientacion').label : ''}
            isExpanded={expandedSections.orientacion}
            onToggle={() => toggleSection('orientacion')}
          >
            {getOptions('orientacion').map(option => (
              <TextChip
                key={option.key}
                option={option}
                selected={isMultiOptionSelected('orientacion', option.key)}
                onClick={() => updateMultiSelect('orientacion', option.key)}
              />
            ))}
          </AccordionSection>

          <AccordionSection
            label={getFilter('altura') ? getFilter('altura').label : ''}
            isExpanded={expandedSections.altura}
            onToggle={() => toggleSection('altura')}
          >
            {getOptions('altura').map(option => (
              <TextChip
                key={option.key}
                option={option}
                selected={isSingleOptionSelected('altura', option.key)}
                onClick={() => updateSingleSelect('altura', option.key)}
              />
            ))}
          </AccordionSection>
        </div>

        <div className={css.footer}>
          <button
            type="button"
            className={css.resetButton}
            onClick={resetManagedFilters}
            disabled={!hasActiveManagedFilters}
          >
            <FormattedMessage id="SearchFiltersModalV2.clear" />
          </button>
          <button type="button" className={css.applyButton} onClick={handleApply}>
            <SlidersIcon />
            <FormattedMessage id="SearchFiltersModalV2.showResults" />
          </button>
        </div>
      </div>
    </div>,
    portalRoot
  );
};

SearchFiltersModalV2.defaultProps = {
  currentSearchParams: {},
  resultsCount: 0,
  priceDistribution: [],
  priceRange: null,
};

SearchFiltersModalV2.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplySearchParams: PropTypes.func.isRequired,
  currentSearchParams: PropTypes.object,
  filterConfig: PropTypes.arrayOf(PropTypes.object).isRequired,
  resultsCount: PropTypes.number,
  intl: intlShape.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  priceDistribution: PropTypes.arrayOf(PropTypes.number),
  priceRange: PropTypes.shape({
    minPrice: PropTypes.number.isRequired,
    maxPrice: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
  }),
};

export default SearchFiltersModalV2;
